import type { CheckinNotificationEmailOptions } from "./schema";
import { getOutreachMaster } from "./outreachMasters";
import { DEFAULT_SUBMISSION_CONFIRMATION_MESSAGE } from "./submissionConfirmation";
import { plainTextToRichText, normalizeRichTextForSave } from "./richText";

export type TestimonialCollectionMethod = "testimonial_page" | "google_reviews";
// Exact identity only: renamed owner relationships are not retired.
export const isRetiredTestimonialRelationship = (name: string) =>
  name.trim().toLowerCase() === "ic - testimonial received";
export const withoutRetiredTestimonialRelationship = (tags: string[]) =>
  tags.filter((tag) => !isRetiredTestimonialRelationship(tag));

export type TestimonialRequestNotification = {
  enabled: boolean;
  emailEnabled: boolean;
  textEnabled: boolean;
  emails: string[];
  mobiles: string[];
  emailSubject: string;
  emailBody: string;
  emailOptions: CheckinNotificationEmailOptions;
  textBody: string;
};

export type TestimonialRequestBasics = {
  headline: string;
  intro: string;
  testimonialLabel: string;
  testimonialPlaceholder: string;
  mediaLabel: string;
  mediaButtonLabel: string;
  consentText: string;
  submitLabel: string;
  confirmationMessage: string;
  confirmationMode?: "message" | "page";
  confirmationUrl?: string;
  collectFirstName: boolean;
  collectLastName: boolean;
  collectPhone: boolean;
  collectAddressLine1: boolean;
  collectAddressLine2: boolean;
  collectCity: boolean;
  collectRegion: boolean;
  collectPostalCode: boolean;
  requiredFirstName: boolean;
  requiredLastName: boolean;
  requiredPhone: boolean;
  requiredAddressLine1: boolean;
  requiredAddressLine2: boolean;
  requiredCity: boolean;
  requiredRegion: boolean;
  requiredPostalCode: boolean;
  relationshipTags: string[];
};

export type TestimonialRequestFlowSettings = {
  collectionMethod: TestimonialCollectionMethod;
  basics: TestimonialRequestBasics;
  template: {
    provenance?: "system" | "custom";
    name: string;
    emailEnabled: boolean;
    subject: string;
    body: string;
    headerImageUrl: string;
    includeSignature: boolean;
    headerAlign: "left" | "center" | "right";
    signatureAlign: "left" | "center" | "right";
    emailOptions: CheckinNotificationEmailOptions;
  };
  notification: TestimonialRequestNotification;
};

export type TestimonialRequestFlowPatch = {
  collectionMethod?: TestimonialCollectionMethod;
  basics?: Partial<TestimonialRequestBasics>;
  template?: Partial<TestimonialRequestFlowSettings["template"]>;
  notification?: Partial<TestimonialRequestNotification>;
};

const reviewMaster = getOutreachMaster("review_request");
const LEGACY_TESTIMONIAL_REQUEST_EMAIL_BODY = `Hi {{firstName}},

I hope you're doing well! It's been a genuine pleasure working with you, and I'd love your help with something quick.

Would you be open to sharing a short testimonial about your experience? A few honest words from you helps other people feel confident about working with me — and it would mean a lot.

The easiest way is right here — this quick form lets you record a video or type a few words in under a minute:

{{testimonialLink}}

A quick video is preferred! People connect with a real face and voice, but a few typed sentences are perfect too.

Not sure what to say? Here are a few prompts to make it easy:

- What was your situation before we worked together?
- What changed for you, or what results did you see?
- What would you tell someone who's on the fence about working with me?

The best testimonials are specific — a real moment, a number, or a clear "before and after" goes a long way.

No pressure at all, and there's no wrong way to do it. Just open the link above whenever you get a chance.

Thank you so much!

[Your name]`;

// Store the initial email as paragraphs rather than a newline-delimited blob.
// This preserves the intentional breathing room when the message is opened in
// the rich-text composer or delivered by an email provider.
export const HISTORICAL_TWO_OPTION_REQUEST_BODY = LEGACY_TESTIMONIAL_REQUEST_EMAIL_BODY
  .replace("\n\n[Your name]", "")
  .replace("\n\nThank you so much!", "\n\nPrefer Google? Leave us a review here: {{googleReviewLink}}\n\nThank you so much!");
export const HISTORICAL_TESTIMONIAL_REQUEST_EMAIL_BODY = HISTORICAL_TWO_OPTION_REQUEST_BODY
  .split(/\n\s*\n/)
  .map((paragraph) => `<p>${paragraph.replace(/\n/g, "<br>")}</p>`)
  .join("<p><br></p>");

export const testimonialEmailRichText = (body: string) => body
  .split(/\n\s*\n/)
  .map((paragraph) => `<p>${plainTextToRichText(paragraph)}</p>`)
  .join("<p><br></p>");
export const TESTIMONIAL_PAGE_REQUEST_BODY = `Hi {{firstName}},

Would you share a testimonial about your experience working with us? Your honest feedback helps others learn what to expect.

Share your experience through our testimonial page:

{{testimonialLink}}

You can write a few words or add a photo or video if you like.

Thank you for your time!`;
export const GOOGLE_REVIEW_REQUEST_BODY = `Hi {{firstName}},

Would you share an honest Google review about your experience working with us? Your feedback helps others learn what to expect.

Leave your Google review here:

{{googleReviewLink}}

Thank you for your time!`;
export const TESTIMONIAL_REQUEST_BODY = `Hi {{firstName}},

I hope you're doing well! It's been a genuine pleasure working with you, and I'd love your help with something quick.

Would you be open to sharing a short testimonial about your experience? A few honest words from you helps other people feel confident about working with me - and it would mean a lot.

Here's where to leave us a review:

{{reviewlink}}

Not sure what to say? Here are a few things to think about:

- What was your situation before we worked together?

- What changed for you, or what results did you see?

- What would you tell someone who's on the fence about working with me?

The best testimonials are specific - a real moment, a number, or a clear "before and after" goes a long way.

No pressure at all, and there's no wrong way to do it. Just open the link above whenever you get a chance.

Thank you so much!`;
export const DEFAULT_TESTIMONIAL_REQUEST_EMAIL_BODY = testimonialEmailRichText(TESTIMONIAL_REQUEST_BODY);
export function testimonialRequestDefault(_method: TestimonialCollectionMethod) {
  return {
    subject: "Would you share your experience?",
    body: DEFAULT_TESTIMONIAL_REQUEST_EMAIL_BODY,
  };
}

/** Invitation-only compatibility: keep owner wording, but follow the selected destination. */
export function normalizeTestimonialReviewLinks(template: string): string {
  return template.replace(/\{\{\s*(?:testimonialLink|googleReviewLink)(\s*\|[^{}]*)?\s*\}\}/gi,
    (_token, fallback = "") => `{{reviewlink${fallback}}}`);
}

// Enumerated system serializers, not HTML stripping or approximate comparison.
export function knownTestimonialBodyRepresentations(body: string): string[] {
  const paragraphs = body.split(/\r?\n\s*\r?\n/).map(p => p.trim()).filter(Boolean);
  const forms = [
    body,
    paragraphs.map(p => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("<p><br></p>"),
    paragraphs.map(p => `<p>${plainTextToRichText(p)}</p>`).join(""),
    testimonialEmailRichText(body),
    `<p>${plainTextToRichText(body)}</p>`,
  ];
  return Array.from(new Set([...forms, ...forms.map(form => normalizeRichTextForSave(form, { allowImages: true }))]));
}

function untouchedTemplate(template: TestimonialRequestFlowSettings["template"]) {
  if (template.provenance === "custom") return false;
  if (template.emailEnabled !== true || template.headerImageUrl !== "" ||
      template.includeSignature !== true || template.headerAlign !== "left" ||
      template.signatureAlign !== "left") return false;
  const options = template.emailOptions;
  if (Object.entries(options).some(([key, value]) => {
    const defaults: Record<string, unknown> = { headerImageUrl: "", includeSignature: true, headerAlign: "left", signatureAlign: "left" };
    return !(key in defaults) || value !== defaults[key];
  })) return false;
  const generations = [
    { subject: "Quick favor {{firstName}}?", body: HISTORICAL_TWO_OPTION_REQUEST_BODY },
    { subject: "Quick favor {{firstName}}?", body: LEGACY_TESTIMONIAL_REQUEST_EMAIL_BODY },
    { subject: "Quick favor {{firstName}}?", body: LEGACY_TESTIMONIAL_REQUEST_EMAIL_BODY.replace("\n\n[Your name]", "") },
    { subject: "Would you share your experience?", body: TESTIMONIAL_PAGE_REQUEST_BODY },
    { subject: "Would you share a Google review?", body: GOOGLE_REVIEW_REQUEST_BODY },
    { subject: "Would you share your experience?", body: TESTIMONIAL_REQUEST_BODY },
  ];
  return generations.some(g => template.subject === g.subject && knownTestimonialBodyRepresentations(g.body).includes(template.body));
}

export function changeTestimonialCollectionMethod(settings: TestimonialRequestFlowSettings, method: TestimonialCollectionMethod): TestimonialRequestFlowSettings {
  const system = untouchedTemplate(settings.template);
  return {
    ...settings,
    collectionMethod: method,
    template: system
      ? { ...settings.template, ...testimonialRequestDefault(method), provenance: "system" }
      : { ...settings.template, provenance: "custom" },
  };
}

/** Apply only owner-changed fields, then derive system copy from server state. */
export function applyTestimonialRequestFlowPatch(current: TestimonialRequestFlowSettings, patch: TestimonialRequestFlowPatch): TestimonialRequestFlowSettings {
  const changedMethod = patch.collectionMethod !== undefined && patch.collectionMethod !== current.collectionMethod;
  const base = changedMethod ? changeTestimonialCollectionMethod(current, patch.collectionMethod!) : current;
  const templatePatch = patch.template?.provenance === "system"
    ? {} : { ...(patch.template ?? {}) };
  // "system" is never authority supplied by a caller. It may accompany the
  // exact generated copy from the UI, but cannot restore a customized draft.
  const generated = changedMethod && base.template.provenance === "system"
    && templatePatch.provenance !== "custom"
    && Object.entries(templatePatch).every(([key, value]) =>
      key === "provenance" || JSON.stringify(value) === JSON.stringify(base.template[key as keyof typeof base.template]));
  const ownerEdit = Object.keys(templatePatch).some(key => key !== "provenance") && !generated;
  delete templatePatch.provenance;
  return mergeTestimonialRequestFlowSettings({
    ...base,
    basics: { ...base.basics, ...patch.basics },
    notification: { ...base.notification, ...patch.notification },
    template: {
      ...base.template,
      ...templatePatch,
      provenance: ownerEdit || patch.template?.provenance === "custom" ? "custom" : base.template.provenance,
    },
  });
}

export const DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS: TestimonialRequestFlowSettings = {
  collectionMethod: "testimonial_page",
  basics: {
    headline: "Share Your Experience",
    intro: "Loved working with us? A short video is the most powerful way to tell your story — but a photo or a few written words mean just as much.",
    testimonialLabel: "Your Testimonial",
    testimonialPlaceholder: "What did you enjoy most about working with us?",
    mediaLabel: "Add a Video or Photo (optional)",
    mediaButtonLabel: "Upload a Photo or Video",
    consentText: "I agree to let this business share my testimonial publicly.",
    submitLabel: "Share My Testimonial",
    confirmationMessage: DEFAULT_SUBMISSION_CONFIRMATION_MESSAGE,
    collectFirstName: true,
    collectLastName: true,
    collectPhone: false,
    collectAddressLine1: false,
    collectAddressLine2: false,
    collectCity: false,
    collectRegion: false,
    collectPostalCode: false,
    requiredFirstName: true,
    requiredLastName: true,
    requiredPhone: false,
    requiredAddressLine1: false,
    requiredAddressLine2: false,
    requiredCity: false,
    requiredRegion: false,
    requiredPostalCode: false,
    relationshipTags: [],
  },
  template: {
    provenance: "system",
    name: reviewMaster.templateName,
    emailEnabled: true,
    subject: testimonialRequestDefault("testimonial_page").subject,
    body: DEFAULT_TESTIMONIAL_REQUEST_EMAIL_BODY,
    headerImageUrl: "",
    includeSignature: true,
    headerAlign: "left",
    signatureAlign: "left",
    emailOptions: {},
  },
  notification: {
    enabled: true,
    emailEnabled: true,
    textEnabled: false,
    emails: [],
    mobiles: [],
    emailSubject: "New testimonial submitted — please review.",
    emailBody: "New testimonial submitted — please review.\n\nReview it here: {{testimonialReviewLink}}",
    emailOptions: {},
    textBody: "New testimonial submitted — please review.\n\nReview it here: {{testimonialReviewLink}}",
  },
};

export function mergeTestimonialRequestFlowSettings(
  value: TestimonialRequestFlowPatch | Partial<TestimonialRequestFlowSettings> | null | undefined,
): TestimonialRequestFlowSettings {
  const input = value ?? {};
  const rawBasics = {
    ...DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS.basics,
    ...(input.basics ?? {}),
  };
  const basics: TestimonialRequestBasics = {
    headline: rawBasics.headline,
    intro: rawBasics.intro,
    testimonialLabel: rawBasics.testimonialLabel,
    testimonialPlaceholder: rawBasics.testimonialPlaceholder,
    mediaLabel: rawBasics.mediaLabel,
    mediaButtonLabel: rawBasics.mediaButtonLabel,
    consentText: rawBasics.consentText,
    submitLabel: rawBasics.submitLabel,
    confirmationMessage: rawBasics.confirmationMessage,
    confirmationMode: rawBasics.confirmationMode,
    confirmationUrl: rawBasics.confirmationUrl,
    collectFirstName: rawBasics.collectFirstName,
    collectLastName: rawBasics.collectLastName,
    collectPhone: rawBasics.collectPhone,
    collectAddressLine1: rawBasics.collectAddressLine1,
    collectAddressLine2: rawBasics.collectAddressLine2,
    collectCity: rawBasics.collectCity,
    collectRegion: rawBasics.collectRegion,
    collectPostalCode: rawBasics.collectPostalCode,
    requiredFirstName: rawBasics.requiredFirstName,
    requiredLastName: rawBasics.requiredLastName,
    requiredPhone: rawBasics.requiredPhone,
    requiredAddressLine1: rawBasics.requiredAddressLine1,
    requiredAddressLine2: rawBasics.requiredAddressLine2,
    requiredCity: rawBasics.requiredCity,
    requiredRegion: rawBasics.requiredRegion,
    requiredPostalCode: rawBasics.requiredPostalCode,
    relationshipTags: withoutRetiredTestimonialRelationship(rawBasics.relationshipTags),
  };
  const merged = {
    ...DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS,
    ...input,
    basics,
    template: {
      ...DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS.template,
      ...(input.template ?? {}),
      emailOptions: {
        ...DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS.template.emailOptions,
        ...(input.template?.emailOptions ?? {}),
      },
    },
    notification: {
      ...DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS.notification,
      ...(input.notification ?? {}),
      emailOptions: {
        ...DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS.notification.emailOptions,
        ...(input.notification?.emailOptions ?? {}),
      },
    },
  };
  // Upgrade only the previous unedited seeded copy. Owner-written messages,
  // including intentionally blank drafts, remain untouched.
  merged.collectionMethod = input.collectionMethod === "google_reviews" ? "google_reviews" : "testimonial_page";
  // Absence of a template is a new system draft; a supplied partial template
  // must prove the full message generation before it can be refreshed.
  if (input.template && !input.template.provenance) {
    merged.template.provenance = input.template.subject !== undefined && input.template.body !== undefined
      && untouchedTemplate({ ...merged.template, provenance: undefined }) ? "system" : "custom";
  }
  if (merged.notification.textBody === "New testimonial submitted — please review: {{testimonialReviewLink}}") {
    merged.notification.textBody = DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS.notification.textBody;
  }
  const normalized = changeTestimonialCollectionMethod(merged, merged.collectionMethod);
  return {
    ...normalized,
    template: {
      ...normalized.template,
      subject: normalizeTestimonialReviewLinks(normalized.template.subject),
      body: normalizeTestimonialReviewLinks(normalized.template.body),
    },
  };
}